import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import customFetch from '../../../../utils/customFetch';
import { CheckIcon } from '@heroicons/react/24/outline';
import NewUserSlideIn from './newUserSlideIn';
import { useNavigation } from 'react-router-dom';
import { toast } from 'react-toastify';

const allUsersQuery = {
  queryKey: ['allUsers'],
  queryFn: async () => {
    const data = await customFetch.get('/users/admin/all-users');
    return data;
  },
};

export const loader = (queryClient) => async () => {
  const { data } = await queryClient.ensureQueryData(allUsersQuery);
  return data;
};

// export const action =
//   (queryClient) =>
//   async ({ request }) => {
//     const formData = await request.formData();
//     const data = Object.fromEntries(formData);
//     try {
//       await customFetch.post('/auth/register', data);
//       queryClient.invalidateQueries(['allUsers']);
//       toast.success('Erfolgreich erstellt!');
//       return redirect('/dashboard/benutzerverwaltung');
//     } catch (error) {
//       toast.error(error?.response?.data?.msg);
//       return error;
//     }
//   };

const UserList = () => {
  const users = useQuery(allUsersQuery).data.data.users;

  const [showSlideIn, setShowSlideIn] = useState(false);
  const queryClient = useQueryClient();

  const handleCreateUser = async (formData) => {
    const data = Object.fromEntries(formData);
    try {
      await customFetch.post('/auth/register', data); // API call to register user
      queryClient.invalidateQueries(['allUsers']); // Invalidate cache for user list
      toast.success('Erfolgreich erstellt!');
      setShowSlideIn(false); // Close the NewUserSlideIn when successful
    } catch (error) {
      toast.error(error?.response?.data?.msg);
    }
  };

  return (
    <>
      <NewUserSlideIn
        showSlideIn={showSlideIn}
        setShowSlideIn={setShowSlideIn}
        onSubmit={handleCreateUser}
      />

      <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Benutzerverwaltung
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Hier sind alle Benutzer aufgelistet.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              className="block rounded-md bg-svisit-primary-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
              onClick={() => setShowSlideIn(true)}
            >
              Benutzer hinzufügen
            </button>
          </div>
        </div>
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Email bestätigt
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Rolle
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                    >
                      <span className="sr-only">Bearbeiten</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {users.map((user) => (
                    <tr key={user.email} className="even:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                        {user.firstName} {user.lastName}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.status === 'active' ? (
                          <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            {user.status}
                          </span>
                        ) : user.status === 'blocked' ? (
                          <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                            {user.status}
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                            {user.status}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.email}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.isVerified ? (
                          <span>
                            <CheckIcon className="h-4 w-4" />
                          </span>
                        ) : (
                          ''
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.role}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                        <a
                          href={`/dashboard/benutzerverwaltung/${user._id}`}
                          className="text-svisit-primary-600 hover:text-svisit-primary-900"
                        >
                          Bearbeiten
                          <span className="sr-only">
                            , {user.firstName} {user.lastName}
                          </span>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserList;
